import React, { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import "./HomeOurTeamSection.css";

// Example image – replace this with your actual image paths
import memberTemplateImage from "../../../assets/home/ourteam/teammember.png";

// -------------------------
// Team data (up to 15 members)
// -------------------------
const teamData = [
  {
    id: 1,
    name: "Aleksandar Milić",
    role: "Senior Digital Design Verification Engineer",
    img: memberTemplateImage,
    info: [
      "7 + yrs ASIC & SoC verification for power-management ICs",
      "Owns vPlan, regressions and ISO 26262 ASIL-D safety flows",
      "Leads 4-8-person verification squads, Scrum facilitator",
      "Top-level envs with 4 independent power domains",
      "Verifies voltage/temperature monitors, SAR ADC, safety engine",
      "Architect of reusable multi-layer SPI UVC family",
      "Deep expertise in constrained-random UVM, SVA & coverage closure",
      "Generates PVS/PVR safety documentation in Jama",
      "Writes mixed C / SystemVerilog tests + Python automation",
      "Skilled with NCsim, VCS, Incisive, Git/SVN/ClearCase",
      "M.Sc. EE (’16) – graduated with honours",
      "Keen mentor; drives internal knowledge-sharing sessions",
    ],
  },
  {
    id: 2,
    name: "Boško Čulibrk",
    role: "Senior Digital Design Engineer",
    img: memberTemplateImage,
    info: [
      "8 yrs RTL design across radar, AI and power devices",
      "Architects DMA, boot-loader, UART, PWM and DSP blocks",
      "Hands-on with DDR4/5 & GDDR6 controllers and timing closure",
      "Performs CDC clean-up, lint (Real-Intent) & synthesis sign-off",
      "Runs Genus, Vivado, VCS & Verdi daily",
      "Basic DFT understanding (scan insertion & checks)",
      "Writes multicore C++ bring-up tests for SoC-level validation",
      "Produces micro-architecture specs & design docs",
      "Experienced in multi-clock-domain glue logic",
      "Fluent in SystemVerilog/Verilog/VHDL, C/C++, Python",
      "Maintains Git/SVN/ClearCase flows for remote teams",
      "Native Serbian, fluent English communicator",
    ],
  },
  {
    id: 3,
    name: "Željko Marinković",
    role: "Senior Digital Verification Engineer",
    img: memberTemplateImage,
    info: [
      "6 + yrs UVM verification of power-supply & sensor chips",
      "Creates full envs, sequences, assertions & coverage models",
      "Integrates real-number models for AMS correlation",
      "Performs Type-C state-machine & I2C EEPROM verification",
      "Runs Jasper Gold formal on clock-management IPs",
      "Coverage analysis and sign-off ownership",
      "Strong Perl/Python scripting for regression automation",
      "Passionate bug-hunter; enjoys root-cause analysis",
      "Fluent in SystemVerilog/UVM & Verdi debug",
      "Bachelor EE – Telecom & IT (’17)",
      "Native Serbian; advanced English",
      "Promotes “clean code” reviews within verification group",
    ],
  },
  {
    id: 4,
    name: "Aleksa Viktorović",
    role: "Senior Digital Design Verification Engineer",
    img: memberTemplateImage,
    info: [
      "4 + yrs ARM & RISC-V IP/SoC verification",
      "Verification owner for UART, performance counters & CSR blocks",
      "Designs mixed C + SV environments & assembly test-suites",
      "Mentors 2 junior engineers and reviews their code",
      "Strong in interrupt, cache & CPU mode-switch scenarios",
      "Implements SVA and functional coverage for scoreboards",
      "Experienced with AMBA AHB/APB, I2C and UART protocols",
      "Runs VCS/Verdi, Xcelium, SimVision & vManager",
      "Fluent in C, SystemVerilog, Python, MATLAB",
      "B.Sc. Physical Electronics (’19) • M.Sc. Telecom Eng. (’20)",
      "Erasmus exchange alumnus – Madrid, 2019",
      "Enjoys automating regressions with Git hooks & scripts",
    ],
  },
  {
    id: 5,
    name: "Marko Kozomora",
    role: "Senior Functional Verification Engineer",
    img: memberTemplateImage,
    info: [
      "3 yrs ASIC/FPGA verification & IP design",
      "Develops UVCs, scoreboards & SVA for AXI/APB/SPI/UART",
      "Leads High-Speed Lighting Interface verification efforts",
      "Built DNN accelerator IP on Zynq-7010 (AXI Lite/Stream)",
      "Implements UART→AXI bridge & embedded firmware",
      "Defines design constraints and performs timing closure",
      "Experienced with Xcelium, vManager, Vivado & ClearCase",
      "Fluent in VHDL, Verilog, SystemVerilog, C/C++",
      "Bachelor EE – Electronics (’18)",
      "Creates project docs & diagrams with LaTeX and Visio",
      "Regularly gives internal workshops on UVM basics",
      "Active participant in regional FPGA hackathons",
    ],
  },
  {
    id: 6,
    name: "Damjan Rakanović",
    role: "Digital Design Engineer",
    img: memberTemplateImage,
    info: [
      "8 + yrs FPGA CNN accelerators & power-management RTL",
      "PhD candidate researching pruning & compression for CNNs",
      "Develops architectures in VHDL with SV DPI co-simulation",
      "Authors bare-metal C drivers auto-generated via Python",
      "Works with multi-power/clock domains & safety features",
      "Publishes papers (Best-Paper at DSD ’18) on CNN hardware",
      "Teaching assistant – digital design & HDL courses",
      "Skilled in timing closure on UltraScale+ & ZCU104 boards",
      "Implements AES256, FIR/DCT & DSP filters RTL",
      "Experienced with Specman e, SystemVerilog & coverage",
      "Strong debugging from board-level to micro-architecture",
      "Enthusiastic mentor for student FPGA projects",
    ],
  },
  {
    id: 7,
    name: "Nikola Miljković",
    role: "Senior ASIC / FPGA Design Engineer",
    img: memberTemplateImage,
    info: [
      "2.5 yrs high-freq RTL up to 1 GHz (5 G modem & networking)",
      "Performs CDC analysis, constraints & synthesis (Genus/Vivado)",
      "Implements 3-port MAC switch on Zynq-7010 board",
      "Writes SDC files and runs Real-Intent Ascent Lint",
      "Experienced with AXI/APB/Avalon/Wishbone, UART, MAC",
      "Fluent in Verilog/SystemVerilog, VHDL, Perl/Bash, Python",
      "M.Sc. EE (in progress) • B.Sc. EE Electronics (’17)",
      "Produces micro-architecture and Visio documentation",
      "Runs regression & coverage in vManager",
      "Strong Git/SVN/ClearCase workflow user",
      "Native Serbian, fluent English",
      "Enjoys cycling and open-source HDL communities",
    ],
  },
  {
    id: 8,
    name: "Luka Savić",
    role: "Senior Design Verification Engineer",
    img: memberTemplateImage,
    info: [
      "2 yrs UVM DV + 1 yr Java/SQL back-end development",
      "M.Sc. GPA 10.0 • B.Sc. GPA 9.07 – top of class",
      "Maintains Jenkins/Docker CI pipelines & test automation",
      "Experienced with OCP, APB, I²C, SMBus, PMBus protocols",
      "Builds Android & Java web applications alongside DV work",
      "Fluent in SV, C/C++, Java, SQL, XML; basic Scala & Bash",
      "Tools: Cadence, IntelliJ, Eclipse, DBVisualizer, Postman",
      "Runs IMC coverage dashboards and DesignSync/Git flows",
      "Completed UVM & SystemVerilog advanced courses",
      "Finished certified Android programming school",
      "Hard-working, proactive and fast learner",
      "Holds category-B driving licence",
    ],
  },
  {
    id: 9,
    name: "Ivan Milošević",
    role: "Senior Digital Design Verification Engineer",
    img: memberTemplateImage,
    info: [
      "10 + yrs IP & SoC DV across DMA, interconnect & NFC subsystems",
      "Leads up to 6 engineers; drafts schedules & coverage strategy",
      "Expert in SystemVerilog, Specman-e & RAL generation",
      "Delivers UVM, RAL & Formal Verification trainings (2016-2020)",
      "Owns SG-DMA and D2D interconnect verification at chip level",
      "Coordinates globally via secure VPN, Slack/Teams dashboards",
      "Proficient with Verdi, SimVision, IMC & vManager",
      "Skilled in AXI, CHI, I3C, OCP, JTAG and mixed MCUs",
      "Bachelor Computer Engineering (’15)",
      "Strong communication & mentoring skills",
      "Organised multiple UVM internships & bootcamps",
      "Fluent English; enjoys technical writing & white-paper reviews",
    ],
  },
  {
    id: 10,
    name: "Nikola Rudi",
    role: "Senior Digital Design Verification Engineer",
    img: memberTemplateImage,
    info: [
      "8 + yrs verification of power-management & Wi-Fi controllers",
      "Creates configurable UVCs, scoreboards & reset monitors",
      "Masters mixed-signal PMU verification and voltage monitors",
      "Authored Wishbone→UART verification env (Master thesis)",
      "Develops RX/TX tests for IEEE 802.11 ax protocol stack",
      "Fluent in SystemVerilog, Specman-e, Python, C/C++",
      "Runs SimVision, vManager, irun & DesignSync flows",
      "M.Sc. & B.Sc. EE Electronics (’19)",
      "Native Serbian, fluent English, basic German",
      "Experienced with Git, SVN, ClearCase & DVT Eclipse",
      "Proficient in Microsoft Office, Visio & LaTeX docs",
      "Advocates clean reset/clock design for robust silicon",
    ],
  },
  {
    id: 11,
    name: "Marian Cole",
    role: "Verification Lead",
    img: memberTemplateImage,
    info: [
      "15 yrs advanced verification & test-bench architecture",
      "Specialist in reusable UVM frameworks and VIP integration",
      "Drives code & functional coverage closure to 100 % targets",
      "Guides junior engineers through complex debug sessions",
      "Introduces modern lint, CDC and formal sign-off flows",
      "Designs scalable Jenkins pipelines for nightly regressions",
      "Experienced with AMBA, PCIe and high-speed serial IP",
      "Advocates documentation-first and clean-code principles",
      "Fluent in SystemVerilog, e-lang & Python automation",
      "Comfortable presenting at international DV conferences",
      "Known for calm leadership and decisive problem solving",
      "Enjoys chess and mountain hiking outside of work",
    ],
  },
];

// Duplicate the team data for seamless looping
const duplicatedData = [...teamData, ...teamData];

const HomeOurTeamSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, {
    margin: "-100px 0px -100px 0px",
    once: false,
  });

  // ---------- State ----------
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [popupMember, setPopupMember] = useState(null);

  // Carousel configuration
  const CARD_WIDTH = 300;
  const GAP = 16;
  const step = CARD_WIDTH + GAP;

  const isMobile = window.innerWidth < 768;
  const effectiveCardWidth = isMobile ? window.innerWidth * 0.9 : CARD_WIDTH;
  const effectiveStep = effectiveCardWidth + GAP;
  const centerOffset = (window.innerWidth - effectiveCardWidth) / 2;
  const offsetPx = isMobile
    ? -currentIndex * window.innerWidth
    : -(currentIndex * effectiveStep);

  // Auto-rotate every 1.5 seconds
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev + 1);
    }, 1500);
    return () => clearInterval(interval);
  }, [isPaused]);

  // Reset index when reaching end of original data
  useEffect(() => {
    if (currentIndex >= teamData.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex(0);
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  // Navigate manually
  const handlePrev = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => (prev - 1 + teamData.length) % teamData.length);
  };

  const handleNext = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    setHoveredId(null);
  };

  const openPopup = (member) => {
    setPopupMember(member);
    setIsPaused(true);
  };

  const closePopup = () => {
    setPopupMember(null);
    setIsPaused(false);
  };

  return (
    <section className="our-team-section ots" ref={sectionRef}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isInView ? 1 : 0 }}
        transition={{ duration: 0.7 }}
      >
        {/* Header */}
        <div className="team-header">
          <span className="team-subtitle">Our Team</span>
          <h2 className="team-title o">Skilled Specialists</h2>
          <button className="see-all-btn">See All Members</button>
        </div>

        {/* Mobile‐only hint */}
        <p className="tap-hint">Tap a card to see more details</p>

        {/* Carousel */}
        <div
          className="team-carousel"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Left arrow */}
          <button
            className="arrow-button left-arrow"
            onClick={handlePrev}
            aria-label="Previous"
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          {/* Visible window */}
          <div className="carousel-window">
            <motion.div
              className="carousel-track"
              style={{ x: offsetPx }}
              animate={{ x: offsetPx }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              {duplicatedData.map((member, index) => (
                <motion.div
                  key={index}
                  className="team-card"
                  role="button"
                  tabIndex={0}
                  onMouseEnter={() => setHoveredId(member.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    scale:
                      hoveredId === null
                        ? 1
                        : hoveredId === member.id
                        ? 1.05
                        : 0.95,
                  }}
                  transition={{ duration: 0.2 }}
                  onClick={() => openPopup(member)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openPopup(member);
                    }
                  }}
                >
                  <div className="card-content">
                    <div className="team-image-wrapper">
                      <img src={member.img} alt={member.name} />
                    </div>
                    <h3>{member.name}</h3>
                    <p className="team-role">{member.role}</p>
                    {/* Explicit “View Details” button on the card */}
                    <button className="info-button">View Details</button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right arrow */}
          <button
            className="arrow-button right-arrow"
            onClick={handleNext}
            aria-label="Next"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {/* Popup details */}
        <AnimatePresence>
          {popupMember && (
            <motion.div
              className="popup-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closePopup}
            >
              <motion.div
                className="popup-content"
                initial={{ scale: 0.7 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.7 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="popup-header">
                  <h3>{popupMember.name}</h3>
                  <button
                    className="popup-close"
                    aria-label="Close"
                    onClick={closePopup}
                  >
                    &times;
                  </button>
                </div>
                <p className="popup-role">{popupMember.role}</p>
                <hr className="popup-divider" />
                <ul className="popup-info">
                  {popupMember.info.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
                <button className="popup-close-btn" onClick={closePopup}>
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default HomeOurTeamSection;